import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  InputBase,
  CircularProgress,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { calculatePayment } from '../customPaymentHook';
import { fetchCouponCodes, validateCoupon } from '../../../../apiCalls/urls/urlFunction';
import {
  couponAppliedReset,
  couponCodeClear,
  couponInputHandlerData,
  couponStatusUpdate,
  setCouponData,
} from '../../../../store/productDetails/action';
import { Close } from '@mui/icons-material';

const CouponDrawer = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = React.useState(false);

  const PRODUCTDETAILS = useSelector((state) => state?.ProductDetail?.allData);
  const DENOMINATION_STORE_DATA = useSelector((state) => state?.Denominations?.allData);
  const COUPON_DATA = useSelector((state) => state?.Denominations?.couponData);
  const dispatch = useDispatch();

  const handleFetchCoupons = async () => {
    const res = await fetchCouponCodes(PRODUCTDETAILS?.id);
    if (res?.status) {
      const data = res?.data?.data;
      dispatch(setCouponData({ coupons: data?.coupons || [], ...COUPON_DATA }));
      setOpen(true);
    } else {
      toast.error(res?.message || 'No coupon available');
    }
  };

  const handleValidateCoupon = async (codeOverride) => {
    setLoading((prev) => !prev);
    const code = typeof codeOverride === 'string' ? codeOverride : COUPON_DATA?.couponCode;

    const calculationResult = calculatePayment(
      DENOMINATION_STORE_DATA,
      PRODUCTDETAILS?.discount,
      PRODUCTDETAILS?.isCashbackProduct
    );

    const payload = {
      couponCode: code,
      productId: PRODUCTDETAILS?.id,
      grossAmount: calculationResult?.netPaymentAmt,
    };

    if (!payload.couponCode) {
      toast.error('coupon code is required.');
      setLoading((prev) => !prev);
      return;
    }

    dispatch(
      couponStatusUpdate({
        isCouponApplied: false,
        couponAppliedCode: null,
        couponBeforeApplyCode: payload?.couponCode,
      })
    );

    const validateResponse = await validateCoupon(payload);
    if (validateResponse?.data?.status) {
      toast.success(validateResponse?.data?.message);
      const couponDiscountTerms = COUPON_DATA?.coupons?.filter(
        (item) => item?.couponCode === payload?.couponCode
      );
      dispatch(
        couponStatusUpdate({
          isCouponApplied: true,
          couponAppliedCode: payload?.couponCode,
          couponBeforeApplyCode: null,
          ...validateResponse?.data?.data,
          ...(couponDiscountTerms?.[0] || {}),
        })
      );
    } else {
      dispatch(
        couponStatusUpdate({
          isCouponApplied: false,
          couponAppliedCode: null,
          couponBeforeApplyCode: payload?.couponCode,
        })
      );
      toast.error(validateResponse?.data?.message);
    }
    setLoading((prev) => !prev);
    return;
  };

  const getDiscountLabel = (coupon) => {
    if (coupon.discountType === 'FLAT') {
      return `₹${coupon.discountValue} OFF`;
    }
    if (coupon.discountType === 'PERCENTAGE') {
      return `${coupon.discountValue}% OFF`;
    }
    return '';
  };

  const applyCouponCodeHandler = (couponCode) => {
    if (!couponCode) {
      toast.error('Apply Coupon Code Again');
      return;
    }
    dispatch(
      couponStatusUpdate({
        isCouponApplied: false,
        couponAppliedCode: null,
        couponBeforeApplyCode: null,
      })
    );
    dispatch(couponInputHandlerData(couponCode));
    handleValidateCoupon(couponCode);
    setOpen(false);
  };

  const manualCoupounHandler = (e) => {
    dispatch(couponInputHandlerData(e.target.value));
    if (COUPON_DATA?.isCouponApplied) dispatch(couponAppliedReset());
  };

  const coupounClearHandler = () => {
    dispatch(couponCodeClear());
    if (COUPON_DATA?.isCouponApplied) dispatch(couponAppliedReset());
  };

  const CoupounCodeCard = ({ coupon }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const discountLabel = getDiscountLabel(coupon);

    // Dynamic Validation Logic inside the component
    const validationState = React.useMemo(() => {
      const calculationResult = calculatePayment(
        DENOMINATION_STORE_DATA,
        PRODUCTDETAILS?.discount,
        PRODUCTDETAILS?.isCashbackProduct
      );

      const minAmount = coupon?.conditions?.minPurchaseAmount || 0;
      const maxAmount = coupon?.conditions?.maxPurchaseAmount || Infinity;

      const isEligible =
        calculationResult.netPaymentAmt >= minAmount &&
        calculationResult.netPaymentAmt <= maxAmount;

      const isApplied =
        COUPON_DATA?.isCouponApplied && COUPON_DATA?.couponCode === coupon?.couponCode;

      return {
        isEligible,
        isApplied,
        isDisable: !isEligible,
        cardBackgroundColor: isEligible ? (isApplied ? '#cccccc' : 'primary.main') : '#ccc',
      };
    }, [DENOMINATION_STORE_DATA, coupon, COUPON_DATA]);

    return (
      <Box
        key={coupon?.couponId}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',

          position: 'relative',
          boxShadow: 3,
          minHeight: { xs: 140, sm: 200 },
        }}
      >
        {/* Coupon Card Layout */}
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            backgroundColor: '#f4f4f4',
            position: 'relative',
          }}
        >
          {/* Discount Stripe */}
          <Box
            sx={[
              {
                width: { xs: 50, sm: 60 },
                backgroundColor: validationState.cardBackgroundColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                height: '100%',
              },
            ]}
          >
            {[32, 45, 58, 71]?.map((item, idx) => {
              return (
                <Box
                  key={`dots-${idx}`}
                  component={'div'}
                  sx={{
                    position: 'absolute',
                    left: -5,
                    top: `${item}%`,
                    transform: 'translateY(-50%)',
                    width: 12,
                    height: 12,
                    backgroundColor: 'white.main',
                    borderRadius: '50%',
                  }}
                />
              );
            })}

            <Typography
              sx={{
                transform: 'rotate(-90deg)',
                fontWeight: 700,
                fontSize: { xs: 14, sm: 16 },
                color: '#fff',
                whiteSpace: 'nowrap',
                letterSpacing: '0.5px',
              }}
            >
              {discountLabel}
            </Typography>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              py: { xs: 1.5, sm: 2 },
              px: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* Top Row - Title & Apply Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 13, sm: 15 },
                  lineHeight: 1.3,
                  flex: 1,
                  color: 'primary.main',
                }}
              >
                {coupon?.coupon_name || 'SPECIAL OFFER'}
              </Typography>

              <Button
                variant="text"
                size="small"
                onClick={() => applyCouponCodeHandler(coupon?.couponCode)}
                sx={{
                  minWidth: { xs: 60, sm: 80 },
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 600,
                  py: 0.5,
                  borderRadius: '20px',
                }}
                disabled={validationState.isDisable || validationState.isApplied}
              >
                {validationState.isApplied ? 'APPLIED' : 'APPLY'}
              </Button>
            </Box>

            {/* Divider */}
            <Divider
              orientation="horizontal"
              sx={{
                borderBottom: '1px dashed',
                borderColor: 'divider',
                my: 0.5,
              }}
            />

            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 11, sm: 12 },
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.5,

                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',

                  WebkitLineClamp: isOpen ? 'unset' : 2,
                }}
              >
                {coupon?.displayText}
              </Typography>

              {/* Read More / Less */}
              {coupon?.displayText?.length > 80 && (
                <Typography
                  component="span"
                  onClick={() => setIsOpen(!isOpen)}
                  sx={{
                    position: isOpen ? 'relative' : 'absolute',
                    bottom: isOpen ? 'auto' : 0,
                    right: 0,

                    cursor: 'pointer',
                    fontSize: { xs: 11, sm: 12 },
                    fontWeight: 600,
                    color: 'primary.main',
                    background: isOpen
                      ? 'transparent'
                      : 'linear-gradient(to right, transparent, #f4f4f4 40%)',
                    pl: 1,
                  }}
                >
                  {isOpen ? 'Read Less' : 'Read More'}
                </Typography>
              )}
            </Box>

            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              sx={{
                mb: 1,
              }}
            >
              {/* Coupoun Code  */}
              <Typography
                sx={{
                  py: 0.5,
                  px: 1,
                  fontSize: { xs: 10, sm: 13 },
                  fontWeight: 500,
                  border: 1.5,
                  borderRadius: 6,
                  borderStyle: 'dashed',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  backgroundColor: 'white.main',
                }}
              >
                {coupon?.couponCode}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={500} color={'primary.main'}>
            Apply Coupon Code
          </Typography>

          <Button
            variant="text"
            sx={{
              textTransform: 'none',
              color: 'textPrimary.main',
              fontWeight: 500,
            }}
            onClick={handleFetchCoupons}
          >
            See All
          </Button>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          overflow="hidden"
          sx={{
            border: '1.5px solid',
            borderColor: 'divider',
            borderRadius: 2,
            py: 0.5,
            height: {
              xs: 34,
              md: 44
            },
            justifyContent: 'space-between',
          }}
        >
          <InputBase
            placeholder="Enter Coupon Code"
            value={COUPON_DATA?.couponCode?.length ? COUPON_DATA?.couponCode : ''}
            onChange={(e) => manualCoupounHandler(e)}
            sx={{ flex: 1, px: 2, fontSize: 14, textTransform: 'uppercase' }}
          />

          <Stack direction="row" alignItems="center" gap={1}>
            {COUPON_DATA?.isCouponApplied && (
              <IconButton onClick={() => coupounClearHandler()} aria-label="Close" size="small">
                <Close />
              </IconButton>
            )}

            <Button
              variant="contained"
              sx={{
                width: {
                  xs: 80,
                  md: 100
                },
                height: {
                  xs: 50,
                  md: 44,
                },
                backgroundColor: '#000',
                px: {
                  xs: 1,
                  md: 4,
                },
                fontWeight: 400,
                textTransform: 'none',
                fontSize: '1.1rem',
                '&:hover': {
                  boxShadow: 4,
                  backgroundColor: '#000',
                },
              }}
              onClick={handleValidateCoupon}
              disabled={loading || COUPON_DATA?.isCouponApplied}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : COUPON_DATA?.isCouponApplied ? (
                'Applied'
              ) : (
                'Apply'
              )}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* DESKTOP DRAWER */}
      {
        <Drawer
          anchor="right"
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 400, md: 480 },
              maxWidth: '100vw',
              borderRadius: 0,
              borderTopLeftRadius: { xs: 0, md: 3 },
              borderTopRightRadius: { xs: 0, md: 3 },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              p: { xs: 2, sm: 3 },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <IconButton
                onClick={() => setOpen(false)}
                sx={{ display: { xs: 'flex', sm: 'none' } }}
                aria-label="Close"
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                onClick={() => setOpen(false)}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
                aria-label="Close"
              >
                <Close />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  fontWeight: 600,
                }}
              >
                Best Offers for You
              </Typography>
            </Box>
            <Divider />

            {/* Coupons List - Scrollable Area */}
            <Box
              sx={{
                height: '100%',
                overflowY: 'auto',
                px: 1,
                py: 3,

                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                rowGap: 3,
              }}
            >
              {COUPON_DATA?.coupons?.map((coupon) => (
                <CoupounCodeCard coupon={coupon} key={coupon?.couponCode} />
              ))}
            </Box>
          </Box>
        </Drawer>
      }
    </>
  );
};

export default CouponDrawer;
